import { type ZodError } from 'zod';

import { normalizeDateToISOString } from '@vestigas/utils';

import { mapImportRows } from './dataImport.parsing';

export type DataImportPreviewIssueSeverity = 'error' | 'warning';

type DataImportPreviewIssueBase = {
  field?: string;
  message: string;
  rowIndex?: number;
  severity: DataImportPreviewIssueSeverity;
};

export type DataImportPreviewLocalIssue = DataImportPreviewIssueBase & {
  source: 'local';
};

export type DataImportPreviewRemoteIssue = DataImportPreviewIssueBase & {
  source: 'remote';
};

export type DataImportPreviewIssue =
  | DataImportPreviewLocalIssue
  | DataImportPreviewRemoteIssue;

export type DataImportPreviewColumn = {
  field: string;
  sourceHeader: string;
};

export type DataImportPreviewRow = {
  rowIndex: number;
  sourceLine: number;
  values: Record<string, unknown>;
};

export type DataImportPreviewModel = {
  columns: DataImportPreviewColumn[];
  issues: DataImportPreviewIssue[];
  rows: DataImportPreviewRow[];
};

export type BuildDataImportPreviewModelInput = {
  allHeaderFields?: readonly string[];
  columnMapping?: Record<string, string>;
  /** Expected-field names whose numeric values are Excel serials/timestamps to render as localized dates. */
  dateFields?: readonly string[];
  fileContent: Array<Record<string, unknown>>;
  issues?: readonly DataImportPreviewIssue[];
  locale?: string;
  /**
   * Unformatted rows backing `fileContent` when the two differ (XLSX):
   * date cells only carry a usable value here, as Excel serial numbers.
   */
  rawFileContent?: Array<Record<string, unknown>>;
};

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Renders a date-column value for the preview. Numbers are unambiguous
 * (Excel serial numbers or timestamps) and become localized dates; strings
 * keep the source formatting the user already saw in their file.
 */
function toPreviewDateValue(
  displayValue: unknown,
  rawValue: unknown,
  dateFormatter: Intl.DateTimeFormat,
): unknown {
  if (typeof rawValue !== 'number') return displayValue;

  const isoDate = normalizeDateToISOString(rawValue);
  if (!ISO_DATE_REGEX.test(isoDate)) return displayValue;

  return dateFormatter.format(new Date(isoDate));
}

function getOrderedMappedFields(
  columnMapping: Record<string, string>,
  allHeaderFields?: readonly string[],
): string[] {
  const mappedFields = Object.keys(columnMapping).filter(
    (field) => columnMapping[field],
  );
  if (!allHeaderFields) return mappedFields;

  const mappedFieldSet = new Set(mappedFields);
  const configuredFieldSet = new Set(allHeaderFields);
  const configuredFields = allHeaderFields.filter((field) =>
    mappedFieldSet.has(field),
  );
  const remainingFields = mappedFields.filter(
    (field) => !configuredFieldSet.has(field),
  );

  return [...configuredFields, ...remainingFields];
}

export function buildDataImportPreviewModel({
  allHeaderFields,
  columnMapping = {},
  dateFields = [],
  fileContent,
  issues = [],
  locale = 'de-DE',
  rawFileContent,
}: BuildDataImportPreviewModelInput): DataImportPreviewModel {
  const fields = getOrderedMappedFields(columnMapping, allHeaderFields);
  const columns = fields.map((field) => ({
    field,
    sourceHeader: columnMapping[field],
  }));
  const mappedRows = mapImportRows(fileContent, columnMapping);
  const mappedRawRows = rawFileContent
    ? mapImportRows(rawFileContent, columnMapping)
    : mappedRows;
  const dateFieldSet = new Set(dateFields);
  // react-doctor-disable-next-line react-doctor/js-hoist-intl -- locale is a call argument
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    // The normalized value is a calendar date at UTC midnight; formatting in
    // local time would shift it a day for timezones west of UTC.
    timeZone: 'UTC',
  });
  const rows = mappedRows.map((row, rowIndex) => ({
    rowIndex,
    sourceLine: rowIndex + 2,
    values: Object.fromEntries(
      fields.map((field) => [
        field,
        dateFieldSet.has(field)
          ? toPreviewDateValue(
              row[field],
              mappedRawRows[rowIndex]?.[field],
              dateFormatter,
            )
          : row[field],
      ]),
    ),
  }));

  return { columns, issues: [...issues], rows };
}

export function normalizeDataImportPreviewIssues(
  error: ZodError,
): DataImportPreviewLocalIssue[] {
  return error.issues.map((issue) => {
    const rowIndex =
      typeof issue.path[0] === 'number' ? issue.path[0] : undefined;
    const field =
      rowIndex !== undefined && typeof issue.path[1] === 'string'
        ? issue.path[1]
        : undefined;

    return {
      ...(rowIndex === undefined ? {} : { rowIndex }),
      ...(field === undefined ? {} : { field }),
      message: issue.message,
      severity: 'error',
      source: 'local',
    };
  });
}

export function mergeDataImportPreviewIssues(
  localIssues: readonly DataImportPreviewLocalIssue[],
  remoteIssues?: readonly DataImportPreviewRemoteIssue[],
): DataImportPreviewIssue[] {
  return [...localIssues, ...(remoteIssues ?? [])];
}

/**
 * Row-schema validation runs per row and cannot express a whole-file cap, so
 * this checks it separately and reports it as a single global issue —
 * `rowIndex`/`field` stay unset, matching a Zod issue whose path is empty.
 * The caller supplies the already-localized message so this stays free of
 * i18n and testable with plain strings.
 */
export function checkMaxRowsExceeded({
  maxRows,
  message,
  rowCount,
}: {
  maxRows?: number;
  message: string;
  rowCount: number;
}): DataImportPreviewLocalIssue[] {
  if (maxRows === undefined || rowCount <= maxRows) return [];

  return [{ message, severity: 'error', source: 'local' }];
}
